"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ReactiveCashflow = void 0;
const mobx_1 = require("mobx");
const lodash_1 = __importDefault(require("lodash"));
const common_utils_1 = require("@riseupil/common-utils");
const consts_1 = require("../consts");
const enums_1 = require("../types/enums");
const utils_1 = require("../utils");
const envelope_1 = require("./envelope");
const variables_1 = require("./variables");
const fixed_tree_1 = require("./fixed-tree");
class ReactiveCashflow {
    constructor(budget, dateLocale = common_utils_1.DateLocales.EN_IL, includedAccountsFilter = []) {
        this.dateLocale = dateLocale;
        this.envelopes = lodash_1.default.map(utils_1.getNonVariableEnvelopes(budget), e => new envelope_1.ReactiveEnvelope(e, budget.lastUpdatedAt, includedAccountsFilter));
        this.variableTransactions = utils_1.getEnvelope(budget, enums_1.EnvelopeType.Variable).actuals;
        this.variableIncomeTransactions = utils_1.getEnvelope(budget, enums_1.EnvelopeType.VariableIncome).actuals;
        this.excluded = budget.excluded;
        this.filteredVariableTransactions = utils_1.applyAccountFilter(utils_1.getEnvelope(budget, enums_1.EnvelopeType.Variable).actuals, includedAccountsFilter);
        this.filteredVariableIncomeTransactions = utils_1.applyAccountFilter(utils_1.getEnvelope(budget, enums_1.EnvelopeType.VariableIncome).actuals, includedAccountsFilter);
        this.budgetDate = budget.budgetDate;
        this.lastUpdatedAt = new Date(budget.lastUpdatedAt);
        this.customerId = budget.customerId;
        this.created = budget.created;
        this.params = budget.params || {};
        this.cashflowStartDay = budget.cashflowStartDay || consts_1.DEFAULT_BUDGET_START_DAY;
        this.trackingCategoryMetadata = budget.trackingCategoryMetadata;
        this.includedAccountsFilter = includedAccountsFilter;
        if (this.hasCashflowBuilderRiseupGoal) {
            const riseupGoalEnvelope = this.envelopes.find(e => e.type === enums_1.EnvelopeType.RiseUpGoal);
            riseupGoalEnvelope.predictedAmount = this.computeRiseupGoal();
        }
    }
    get isPredictedIncome() {
        return lodash_1.default.isNumber(this.variableIncomePredictionAmount);
    }
    get incomeEnvelopes() {
        return lodash_1.default.filter(this.envelopes, e => !e.isExpense);
    }
    get expenseEnvelopes() {
        return lodash_1.default.filter(this.envelopes, e => e.isExpense);
    }
    get incomeEnvelopesBalanceSum() {
        return lodash_1.default.sumBy(this.incomeEnvelopes, e => e.balancedAmount);
    }
    get incomeEnvelopesOriginalSum() {
        return lodash_1.default.sumBy(this.incomeEnvelopes, e => e.predictedAmount);
    }
    get incomeEnvelopesActualSum() {
        return lodash_1.default.sumBy(this.incomeEnvelopes, e => e.actualsAmount);
    }
    get incomeEnvelopesFilteredActualSum() {
        return lodash_1.default.sumBy(this.incomeEnvelopes, e => e.filteredActualsAmount);
    }
    get expenseEnvelopesBalanceSum() {
        return lodash_1.default.sumBy(this.expenseEnvelopes, e => e.balancedAmount);
    }
    get expenseEnvelopesOriginalSum() {
        return lodash_1.default.sumBy(this.expenseEnvelopes, e => e.predictedAmount);
    }
    get expenseEnvelopesActualSum() {
        return lodash_1.default.sumBy(this.expenseEnvelopes, e => e.actualsAmount);
    }
    get filteredExpenseEnvelopesActualSum() {
        return lodash_1.default.sumBy(this.expenseEnvelopes, e => e.filteredActualsAmount);
    }
    get oneTimeSavingEnvelopes() {
        return lodash_1.default.chain(this.getEnvelopesByType(enums_1.EnvelopeType.TrackingCategory))
            .filter(e => { var _a; return ((_a = e.details.trackingCategory) === null || _a === void 0 ? void 0 : _a.trackingCategoryType) === enums_1.EnvelopeType.Saving; })
            .value();
    }
    get oneTimeSavingActualSum() {
        return lodash_1.default.sumBy(this.oneTimeSavingEnvelopes, e => e.actualsAmount);
    }
    get variableIncomeSum() {
        return this.variableIncomeActualSum;
    }
    get filteredVariableIncomeSum() {
        return this.filteredVariableIncomeActualSum;
    }
    get variableIncomeActualSum() {
        return utils_1.sumTransactions(this.variableIncomeTransactions);
    }
    get filteredVariableIncomeActualSum() {
        return utils_1.sumTransactions(this.filteredVariableIncomeTransactions);
    }
    get variableIncomeOriginalAmount() {
        // This value shouldn't be used, because the variable income is not effected by income envelopes,
        // and this legacy from old predicted income.
        return this.isPredictedIncome ? -(this.adjustedIncomePredictionAmount + this.incomeEnvelopesOriginalSum) : 0;
    }
    get adjustedIncomePredictionAmount() {
        if (this.variableIncomePredictionAmount) {
            return this.variableIncomePredictionAmount;
        }
        return 0;
    }
    get remainingIncomePrediction() {
        return Math.min(0, this.variableIncomeBalancedAmount - this.variableIncomeActualSum);
    }
    get variableIncomeBalancedAmount() {
        if (this.variableIncomePredictionAmount) {
            return Math.min(this.variableIncomeActualSum, this.variableIncomePredictionAmount);
        }
        return this.variableIncomeActualSum;
    }
    get variableOriginalAmount() {
        return -(this.adjustedIncomePredictionAmount + this.incomeEnvelopesOriginalSum + this.expenseEnvelopesOriginalSum);
    }
    get variableBalancedAmount() {
        return -(this.variableIncomeBalancedAmount + this.incomeEnvelopesBalanceSum + this.expenseEnvelopesBalanceSum);
    }
    get variableActualSum() {
        return utils_1.sumTransactions(this.variableTransactions);
    }
    get filteredVariableActualSum() {
        return utils_1.sumTransactions(this.filteredVariableTransactions);
    }
    get remainingVariables() {
        return this.variableBalancedAmount - this.variableActualSum;
    }
    get variableEnvelope() {
        return {
            id: `${this.budgetDate}#${consts_1.VARIABLE_ENVELOPE_ID}`,
            originalAmount: this.variableOriginalAmount,
            balancedAmount: this.variableBalancedAmount,
            actuals: lodash_1.default.cloneDeep(this.variableTransactions),
            type: enums_1.EnvelopeType.Variable,
        };
    }
    get variableIncomeEnvelope() {
        var _a;
        return {
            id: `${this.budgetDate}#${consts_1.VARIABLE_INCOME_ENVELOPE_ID}`,
            originalAmount: this.variableIncomeOriginalAmount,
            balancedAmount: this.variableIncomeBalancedAmount,
            actuals: lodash_1.default.cloneDeep(this.variableIncomeTransactions),
            type: enums_1.EnvelopeType.VariableIncome,
            details: (_a = utils_1.getEnvelope(this, enums_1.EnvelopeType.VariableIncome)) === null || _a === void 0 ? void 0 : _a.details,
        };
    }
    get incomeBalanceSum() {
        return this.incomeEnvelopesBalanceSum + this.variableIncomeBalancedAmount;
    }
    get incomeActualSum() {
        return this.incomeEnvelopesActualSum + this.variableIncomeActualSum;
    }
    get filteredIncomeActualSum() {
        return this.incomeEnvelopesFilteredActualSum + this.filteredVariableIncomeActualSum;
    }
    get expenseBalanceSum() {
        return this.expenseEnvelopesBalanceSum + this.variableActualSum;
    }
    get expenseActualSum() {
        return this.expenseEnvelopesActualSum + this.variableActualSum;
    }
    get filteredExpenseActualSum() {
        return this.filteredExpenseEnvelopesActualSum + this.filteredVariableActualSum;
    }
    get balance() {
        return this.incomeActualSum + this.expenseActualSum;
    }
    get predictedBalance() {
        return (this.incomeBalanceSum + this.expenseBalanceSum) - this.riseupGoal;
    }
    get variableIncomePredictionAmount() {
        return this.params.variableIncomePredictionAmount;
    }
    get riseupGoalEnvelope() {
        return lodash_1.default.find(this.envelopes, e => e.type === enums_1.EnvelopeType.RiseUpGoal);
    }
    get riseupGoal() {
        return (this.riseupGoalEnvelope && this.riseupGoalEnvelope.balancedAmount) || 0;
    }
    get hasCashflowBuilderRiseupGoal() {
        return this.riseupGoalEnvelope && this.riseupGoalEnvelope.details && this.riseupGoalEnvelope.details.averageMonthlySavings !== undefined;
    }
    get fixedTree() {
        return new fixed_tree_1.FixedTree(this.envelopes);
    }
    get weeklyVariables() {
        return new variables_1.Variables(this, this.dateLocale, this.includedAccountsFilter);
    }
    get allTransactions() {
        return [
            ...this.variableTransactions,
            ...this.variableIncomeTransactions,
            ...lodash_1.default.flatMap(this.envelopes, e => e.actuals),
            ...this.excluded,
        ];
    }
    get balancedRiseupGoal() {
        return this.remainingVariables < 0 ? this.riseupGoal + this.remainingVariables : this.riseupGoal;
    }
    get medianPastThreeMonthsExpenses() {
        var _a, _b;
        if (lodash_1.default.isNil((_b = (_a = this.riseupGoalEnvelope) === null || _a === void 0 ? void 0 : _a.details) === null || _b === void 0 ? void 0 : _b.medianMonthlyExpenses)) {
            return null;
        }
        return this.riseupGoalEnvelope.details.medianMonthlyExpenses;
    }
    get riseupGoalRecommendation() {
        var _a, _b, _c;
        if (lodash_1.default.isNil(this.medianPastThreeMonthsExpenses)) {
            return (_c = (_b = (_a = this.riseupGoalEnvelope) === null || _a === void 0 ? void 0 : _a.details) === null || _b === void 0 ? void 0 : _b.averageMonthlySavings) !== null && _c !== void 0 ? _c : 0;
        }
        return Math.max(-this.incomeBalanceSum - this.medianPastThreeMonthsExpenses, 10);
    }
    get balancedRiseupGoalRecommendation() {
        if (lodash_1.default.isNil(this.riseupGoalRecommendation)) {
            return null;
        }
        return -this.predictedBalance < this.riseupGoalRecommendation ? -this.predictedBalance : this.riseupGoalRecommendation;
    }
    computeRiseupGoal() {
        const { riseupGoalInputAmount } = this.riseupGoalEnvelope.details;
        if (lodash_1.default.isNumber(riseupGoalInputAmount)) {
            return riseupGoalInputAmount;
        }
        return this.riseupGoalRecommendation;
    }
    toJson() {
        const envelopes = lodash_1.default.concat(lodash_1.default.map(this.envelopes, e => e.toJson()), [this.variableEnvelope], [this.variableIncomeEnvelope]);
        return {
            envelopes,
            excluded: lodash_1.default.cloneDeep(this.excluded),
            budgetDate: this.budgetDate,
            lastUpdatedAt: this.lastUpdatedAt,
            customerId: this.customerId,
            created: this.created,
            params: lodash_1.default.cloneDeep(this.params),
            cashflowStartDay: this.cashflowStartDay,
            trackingCategoryMetadata: lodash_1.default.cloneDeep(this.trackingCategoryMetadata),
        };
    }
    getCashculations() {
        return {
            // balance
            riseupGoal: this.riseupGoal,
            riseupGoalRecommendation: this.riseupGoalRecommendation,
            balancedRiseupGoal: this.balancedRiseupGoal,
            balancedRiseupGoalRecommendation: this.balancedRiseupGoalRecommendation,
            balance: this.balance,
            predictedBalance: this.predictedBalance,
            isPredictedIncome: this.isPredictedIncome,
            // expense
            expenseActualSum: this.expenseActualSum,
            filteredExpenseActualSum: this.filteredExpenseActualSum,
            expenseBalanceSum: this.expenseBalanceSum,
            // variable expense
            variableActualSum: this.variableActualSum,
            filteredVariableActualSum: this.filteredVariableActualSum,
            variableBalancedAmount: this.variableBalancedAmount,
            // expense envelopes
            expenseEnvelopesActualSum: this.expenseEnvelopesActualSum,
            expenseEnvelopesFilteredActualSum: this.filteredExpenseEnvelopesActualSum,
            expenseEnvelopesBalanceSum: this.expenseEnvelopesBalanceSum,
            // fixed expense envelops
            fixedExpenseEnvelopesActualSum: this.fixedTree.fixedExpenseEnvelopesActualSum
                + this.fixedTree.fixedSavingEnvelopesActualSum,
            fixedExpenseEnvelopesFilteredActualSum: this.fixedTree.fixedExpenseEnvelopesFilteredActualSum
                + this.fixedTree.fixedSavingEnvelopesFilteredActualSum,
            fixedExpenseEnvelopesBalanceSum: this.fixedTree.fixedExpenseEnvelopesBalanceSum
                + this.fixedTree.fixedSavingEnvelopesBalanceSum,
            fixedExpenseEnvelopesPredictedSum: this.fixedTree.fixedExpenseEnvelopesPredictedSum
                + this.fixedTree.fixedSavingEnvelopesPredictedSum,
            // saving
            savingActualSum: this.fixedTree.fixedSavingEnvelopesActualSum
                + this.oneTimeSavingActualSum,
            // income
            incomeActualSum: this.incomeActualSum,
            filteredIncomeActualSum: this.filteredIncomeActualSum,
            incomeBalanceSum: this.incomeBalanceSum,
            // variable income
            variableIncomeActualSum: this.variableIncomeActualSum,
            filteredVariableIncomeActualSum: this.filteredVariableIncomeActualSum,
            variableIncomeBalanceSum: this.variableIncomeBalancedAmount,
            variableIncomePredictionAmount: this.variableIncomePredictionAmount,
            // income envelopes
            incomeEnvelopesActualSum: this.incomeEnvelopesActualSum,
            incomeEnvelopesFilteredActualSum: this.incomeEnvelopesFilteredActualSum,
            incomeEnvelopesBalanceSum: this.incomeEnvelopesBalanceSum,
            incomeEnvelopesPredictedSum: this.incomeEnvelopesOriginalSum,
            // fixed income envelopes
            fixedIncomeEnvelopesActualSum: this.fixedTree.fixedIncomeEnvelopesActualSum,
            fixedIncomeEnvelopesBalanceSum: this.fixedTree.fixedIncomeEnvelopesBalanceSum,
            fixedIncomeEnvelopesPredictedSum: this.fixedTree.fixedIncomeEnvelopesPredictedSum,
            weeklyVariablesCashculations: this.weeklyVariables.getCashculations(),
            envelopes: lodash_1.default.map(this.envelopes, e => e.cashculation),
        };
    }
    getEnvelopesByType(envelopeType) {
        return lodash_1.default.chain(this.envelopes).filter(e => e.type === envelopeType).value();
    }
    setVariableIncomePredictionAmount(amount) {
        this.params.variableIncomePredictionAmount = amount;
    }
}
__decorate([
    mobx_1.observable
], ReactiveCashflow.prototype, "envelopes", void 0);
__decorate([
    mobx_1.observable
], ReactiveCashflow.prototype, "variableTransactions", void 0);
__decorate([
    mobx_1.observable
], ReactiveCashflow.prototype, "variableIncomeTransactions", void 0);
__decorate([
    mobx_1.observable
], ReactiveCashflow.prototype, "excluded", void 0);
__decorate([
    mobx_1.observable
], ReactiveCashflow.prototype, "filteredVariableTransactions", void 0);
__decorate([
    mobx_1.observable
], ReactiveCashflow.prototype, "filteredVariableIncomeTransactions", void 0);
__decorate([
    mobx_1.observable
], ReactiveCashflow.prototype, "budgetDate", void 0);
__decorate([
    mobx_1.observable
], ReactiveCashflow.prototype, "lastUpdatedAt", void 0);
__decorate([
    mobx_1.observable
], ReactiveCashflow.prototype, "customerId", void 0);
__decorate([
    mobx_1.observable
], ReactiveCashflow.prototype, "created", void 0);
__decorate([
    mobx_1.observable
], ReactiveCashflow.prototype, "params", void 0);
__decorate([
    mobx_1.observable
], ReactiveCashflow.prototype, "trackingCategoryMetadata", void 0);
__decorate([
    mobx_1.observable
], ReactiveCashflow.prototype, "includedAccountsFilter", void 0);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "isPredictedIncome", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "incomeEnvelopes", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "expenseEnvelopes", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "incomeEnvelopesBalanceSum", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "incomeEnvelopesOriginalSum", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "incomeEnvelopesActualSum", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "incomeEnvelopesFilteredActualSum", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "expenseEnvelopesBalanceSum", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "expenseEnvelopesOriginalSum", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "expenseEnvelopesActualSum", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "filteredExpenseEnvelopesActualSum", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "oneTimeSavingEnvelopes", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "oneTimeSavingActualSum", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "variableIncomeSum", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "filteredVariableIncomeSum", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "variableIncomeActualSum", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "filteredVariableIncomeActualSum", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "variableIncomeOriginalAmount", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "adjustedIncomePredictionAmount", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "remainingIncomePrediction", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "variableIncomeBalancedAmount", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "variableOriginalAmount", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "variableBalancedAmount", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "variableActualSum", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "filteredVariableActualSum", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "remainingVariables", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "variableEnvelope", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "variableIncomeEnvelope", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "incomeBalanceSum", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "incomeActualSum", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "filteredIncomeActualSum", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "expenseBalanceSum", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "expenseActualSum", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "filteredExpenseActualSum", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "balance", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "predictedBalance", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "variableIncomePredictionAmount", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "riseupGoalEnvelope", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "riseupGoal", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "hasCashflowBuilderRiseupGoal", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "fixedTree", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "weeklyVariables", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "allTransactions", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "balancedRiseupGoal", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "medianPastThreeMonthsExpenses", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "riseupGoalRecommendation", null);
__decorate([
    mobx_1.computed
], ReactiveCashflow.prototype, "balancedRiseupGoalRecommendation", null);
exports.ReactiveCashflow = ReactiveCashflow;
//# sourceMappingURL=cashflow.js.map